/**
 * The minimal takeover backdrop — the app's own dark surface lit by ONE very soft
 * pool of light from the top, blurred so it reads diffuse, carried by light film
 * grain. Picked from the "Halo" exploration (/styleguide/minimal-takeover); it
 * replaces the heavier "Nightfall" navy off-axis bloom — calmer, more minimal.
 *
 * The single shared backdrop for every surface that opens in the room — the Play
 * takeover + entrance, the split login, and onboarding — so changing it here changes
 * all of them at once. Pure presentational markup (no hooks, no client APIs), so it
 * stays RSC-safe and can be imported by server components (StepShell) and client
 * components (login, the takeover) alike.
 *
 * Consumers wrap it in a `.dark` subtree, so `var(--background)` resolves to the
 * app's dark surface (#171717). GOTCHA: a background SVG can't inherit currentColor,
 * so the grain is monochrome (drawn grey, blended); the top light is plain white via
 * color-mix (it's light, not a theme color).
 */

import type { CSSProperties } from "react";

const GRAIN_URL =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'>` +
      `<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch' seed='7'/>` +
      `<feColorMatrix type='saturate' values='0'/></filter>` +
      `<rect width='100%' height='100%' filter='url(#n)'/></svg>`,
  );

interface DarkRoomProps {
  /** The indigo "dawn" glow rising from below the fold — the warm tell from the
   *  marketing site (excellent.so). Opt-in: the Play/onboarding rooms stay flat. */
  dawn?: boolean;
  /** A soft vignette so content floats and the edges fall toward black. */
  vignette?: boolean;
  /** The faint 8-star sparkle night sky (login front door only). */
  starfield?: boolean;
}

/**
 * The shared takeover backdrop. The flat #171717 app floor + one soft top light
 * + film grain (the calm "Halo" base used by Play). The first-run surfaces
 * (login · welcome · onboarding) opt into the marketing-site tells — the indigo
 * `dawn`, a `vignette`, and the front-door `starfield` — so the seam from
 * www.excellent.so → product disappears. All three default OFF, so existing
 * callers (Play takeover/entrance, sales setup) are unchanged.
 */
export function DarkRoom({ dawn = false, vignette = false, starfield = false }: DarkRoomProps = {}) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {/* the flat app-color floor */}
      <div className="absolute inset-0" style={{ background: "var(--background)" }} />
      {/* one very soft light from the top — on an oversized blurred layer so it reads diffuse */}
      <div className="absolute -inset-[12%]" style={{ filter: "blur(58px)" }}>
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 88% at 50% -16%, color-mix(in srgb, #ffffff 13%, transparent) 0%, transparent 60%)",
          }}
        />
      </div>
      {/* the indigo dawn horizon, rising from below the fold (the warm tell) */}
      {dawn && (
        <div className="absolute -inset-x-[15%] -bottom-[38%] h-[88%]" style={{ filter: "blur(78px)" }}>
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(64% 60% at 50% 100%, rgba(99,102,241,0.20), transparent 62%)" }}
          />
        </div>
      )}
      {/* the 8-star sparkle night sky (front door) */}
      {starfield && <StarField />}
      {/* light film grain — overlay lifts AND crushes pixels; kills banding */}
      <div
        className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
        style={{ backgroundImage: `url("${GRAIN_URL}")`, backgroundSize: "200px 200px" }}
      />
      {/* vignette — content floats, edges fall toward black */}
      {vignette && (
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(120% 95% at 50% 30%, transparent 58%, rgba(0,0,0,0.55))" }}
        />
      )}
    </div>
  );
}

/* ── The starfield ───────────────────────────────────────────────────────────
 * The Excellent mark is eight four-pointed sparkles, so the front-door backdrop
 * is that same sparkle scattered faint across the night. Ported from the site's
 * /preview hero. Deterministic: positions/sizes/timings come from one seeded
 * PRNG computed once at module load, every float rounded so SSR and client
 * render byte-identically (no hydration mismatch). No client JS — the twinkle is
 * pure CSS (`.first-run-star*` keyframes in globals.css). Internal, not
 * exported: DarkRoom is the only consumer. */
const STAR_PATH =
  "M29.187 0.461C29.337 0 29.989 0 30.139 0.461L37.098 21.906C37.148 22.058 37.267 22.178 37.419 22.227L58.866 29.188C59.327 29.338 59.327 29.99 58.866 30.139L37.419 37.099C37.267 37.148 37.148 37.268 37.098 37.42L30.139 58.866C29.989 59.327 29.337 59.327 29.187 58.866L22.227 37.42C22.177 37.268 22.058 37.148 21.905 37.099L0.461 30.139C0 29.99 0 29.338 0.461 29.188L21.905 22.227C22.058 22.178 22.177 22.058 22.227 21.906L29.187 0.461Z";

function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rnd2 = (n: number, d: number) => Number(n.toFixed(d));
const STAR_ENVS = ["a", "b", "c", "d"] as const;

interface Star {
  x: number;
  y: number;
  size: number;
  base: number;
  rot: number;
  env: (typeof STAR_ENVS)[number];
  pdur: number;
  pdelay: number;
  sdur: number;
  sdelay: number;
  shim: number;
}

const STARS: Star[] = (() => {
  const rng = mulberry32(0x5ce11e);
  const out: Star[] = [];
  for (let i = 0; i < 110; i++) {
    const small = rng() ** 2.1;
    const faint = rng() ** 1.5;
    const sdur = rnd2(2.4 + rng() * 2.8, 2);
    const pdur = rnd2(9 + rng() * 13, 2);
    const e = rng();
    out.push({
      x: rnd2(rng() * 100, 3),
      y: rnd2(rng() * 72, 3), // bias to the upper band, behind the hero
      size: rnd2(3 + small * 12, 2),
      base: rnd2(0.1 + faint * 0.6, 3),
      rot: rnd2((rng() - 0.5) * 30, 1),
      env: e < 0.34 ? STAR_ENVS[0] : e < 0.6 ? STAR_ENVS[1] : e < 0.8 ? STAR_ENVS[3] : STAR_ENVS[2],
      pdur,
      pdelay: rnd2(-(rng() * pdur), 2),
      sdur,
      sdelay: rnd2(-(rng() * sdur), 2),
      shim: rnd2(0.5 + rng() * 0.22, 2),
    });
  }
  return out;
})();

function StarField() {
  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden>
      {STARS.map((s, i) => (
        <span
          key={i}
          className="first-run-star"
          data-env={s.env}
          style={
            {
              left: `${s.x}%`,
              top: `${s.y}%`,
              width: s.size,
              height: s.size,
              "--base": s.base,
              "--rot": `${s.rot}deg`,
              "--shim": s.shim,
              "--pdur": `${s.pdur}s`,
              "--pdelay": `${s.pdelay}s`,
              "--sdur": `${s.sdur}s`,
              "--sdelay": `${s.sdelay}s`,
            } as CSSProperties
          }
        >
          <svg className="first-run-star-core" viewBox="0 0 59.327 59.327">
            <path d={STAR_PATH} />
          </svg>
        </span>
      ))}
    </div>
  );
}

/* ── The product brandmark ───────────────────────────────────────────────────
 * The real Excellent logo (a 3×3 grid of four-pointed stars, middle-right slot
 * empty). Inlined here because packages/ui cannot reach the shell's
 * _chrome/logo.tsx without a circular dep — this is the single packages/ui copy of
 * the path, shared by the takeover, the entrance, and onboarding. Mark-only; fills
 * with currentColor, so a `.dark` host paints it near-white. */
const LOGO_PATH =
  "M131.369 102.245C131.519 101.784 132.171 101.784 132.321 102.245L139.28 123.69C139.33 123.842 139.449 123.962 139.601 124.011L161.048 130.972C161.509 131.122 161.509 131.774 161.048 131.923L139.601 138.883C139.449 138.932 139.33 139.052 139.28 139.204L132.321 160.65C132.171 161.111 131.519 161.111 131.369 160.65L124.409 139.204C124.359 139.052 124.24 138.932 124.087 138.883L102.643 131.923C102.182 131.774 102.182 131.122 102.643 130.972L124.087 124.011C124.24 123.962 124.359 123.842 124.409 123.69L131.369 102.245Z M80.967 102.245C81.1166 101.784 81.7686 101.784 81.9182 102.245L88.8778 123.69C88.9273 123.842 89.0467 123.962 89.1991 124.011L110.646 130.972C111.107 131.122 111.107 131.774 110.646 131.923L89.1991 138.883C89.0467 138.932 88.9273 139.052 88.8778 139.204L81.9182 160.65C81.7686 161.111 81.1166 161.111 80.967 160.65L74.0062 139.204C73.9568 139.052 73.8373 138.932 73.685 138.883L52.2406 131.923C51.7798 131.774 51.7798 131.122 52.2406 130.972L73.685 124.011C73.8373 123.962 73.9568 123.842 74.0062 123.69L80.967 102.245Z M30.1916 102.245C30.3412 101.784 30.9932 101.784 31.1428 102.245L38.1024 123.69C38.1519 123.842 38.2713 123.962 38.4237 124.011L59.8703 130.972C60.3312 131.122 60.3311 131.774 59.8703 131.923L38.4237 138.883C38.2713 138.932 38.1519 139.052 38.1024 139.204L31.1428 160.65C30.9932 161.111 30.3412 161.111 30.1916 160.65L23.2308 139.204C23.1814 139.052 23.062 138.932 22.9096 138.883L1.46526 131.923C1.0044 131.774 1.00438 131.122 1.46523 130.972L22.9096 124.011C23.062 123.962 23.1814 123.842 23.2308 123.69L30.1916 102.245Z M131.369 1.46532C131.519 1.00446 132.171 1.00447 132.321 1.46534L139.28 22.9108C139.33 23.0632 139.449 23.1826 139.601 23.232L161.048 30.1928C161.509 30.3424 161.509 30.9944 161.048 31.144L139.601 38.1037C139.449 38.1531 139.33 38.2725 139.28 38.4249L132.321 59.8703C132.171 60.3312 131.519 60.3312 131.369 59.8704L124.409 38.4249C124.359 38.2725 124.24 38.1531 124.087 38.1037L102.643 31.144C102.182 30.9944 102.182 30.3424 102.643 30.1928L124.087 23.232C124.24 23.1826 124.359 23.0632 124.409 22.9108L131.369 1.46532Z M80.967 1.46532C81.1166 1.00446 81.7686 1.00447 81.9182 1.46534L88.8778 22.9108C88.9273 23.0632 89.0467 23.1826 89.1991 23.232L110.646 30.1928C111.107 30.3424 111.107 30.9944 110.646 31.144L89.1991 38.1037C89.0467 38.1531 88.9273 38.2725 88.8778 38.4249L81.9182 59.8703C81.7686 60.3312 81.1166 60.3312 80.967 59.8704L74.0062 38.4249C73.9568 38.2725 73.8373 38.1531 73.685 38.1037L52.2406 31.144C51.7798 30.9944 51.7798 30.3424 52.2406 30.1928L73.685 23.232C73.8373 23.1826 73.9568 23.0632 74.0062 22.9108L80.967 1.46532Z M30.1916 1.46532C30.3412 1.00446 30.9932 1.00447 31.1428 1.46534L38.1024 22.9108C38.1519 23.0632 38.2713 23.1826 38.4237 23.232L59.8703 30.1928C60.3312 30.3424 60.3311 30.9944 59.8703 31.144L38.4237 38.1037C38.2713 38.1531 38.1519 38.2725 38.1024 38.4249L31.1428 59.8703C30.9932 60.3312 30.3412 60.3312 30.1916 59.8704L23.2308 38.4249C23.1814 38.2725 23.062 38.1531 22.9096 38.1037L1.46526 31.144C1.0044 30.9944 1.00438 30.3424 1.46523 30.1928L22.9096 23.232C23.062 23.1826 23.1814 23.0632 23.2308 22.9108L30.1916 1.46532Z M30.1916 52.2339C30.3412 51.773 30.9932 51.773 31.1428 52.2339L38.1024 73.6794C38.1519 73.8317 38.2713 73.9511 38.4237 74.0006L59.8703 80.9614C60.3312 81.111 60.3311 81.763 59.8703 81.9125L38.4237 88.8722C38.2713 88.9216 38.1519 89.0411 38.1024 89.1935L31.1428 110.639C30.9932 111.1 30.3412 111.1 30.1916 110.639L23.2308 89.1934C23.1814 89.0411 23.062 88.9216 22.9096 88.8722L1.46526 81.9125C1.0044 81.763 1.00438 81.111 1.46523 80.9614L22.9096 74.0006C23.062 73.9511 23.1814 73.8317 23.2308 73.6794L30.1916 52.2339Z M80.967 52.2339C81.1166 51.773 81.7686 51.773 81.9182 52.2339L88.8778 73.6794C88.9273 73.8317 89.0467 73.9511 89.1991 74.0006L110.646 80.9614C111.107 81.111 111.107 81.763 110.646 81.9125L89.1991 88.8722C89.0467 88.9216 88.9273 89.0411 88.8778 89.1935L81.9182 110.639C81.7686 111.1 81.1166 111.1 80.967 110.639L74.0062 89.1934C73.9568 89.0411 73.8373 88.9216 73.685 88.8722L52.2406 81.9125C51.7798 81.763 51.7798 81.111 52.2406 80.9614L73.685 74.0006C73.8373 73.9511 73.9568 73.8317 74.0062 73.6794L80.967 52.2339Z";

/** The real product logo, mark-only. Fills with currentColor. */
export function BrandMark({ size = 22, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 163 163"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={className}
    >
      <path d={LOGO_PATH} fill="currentColor" />
    </svg>
  );
}

/** The "Excellent" wordmark — our logotype (public/excellent-logo-type.svg, the
 *  165×35 artwork), NOT the name set in body text. Recolored to `currentColor`
 *  via a CSS mask so it renders in the surface's text color; sized by `height`,
 *  width follows the artwork ratio. Mirrors the marketing site's `LogoType` so
 *  the seam from www.excellent.so → product disappears. */
export function Wordmark({ height = 15, className }: { height?: number; className?: string }) {
  const width = Math.round((height * 165) / 35);
  return (
    <span
      role="img"
      aria-label="Excellent"
      className={className}
      style={{
        display: "inline-block",
        width,
        height,
        backgroundColor: "currentColor",
        maskImage: "url(/excellent-logo-type.svg)",
        WebkitMaskImage: "url(/excellent-logo-type.svg)",
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
        maskSize: "contain",
        WebkitMaskSize: "contain",
        maskPosition: "left center",
        WebkitMaskPosition: "left center",
      }}
    />
  );
}

/** The logo lockup — the mark + the "Excellent" logotype (the wordmark artwork,
 *  never set text), spaced like the marketing site (excellent.so). The top-left
 *  brand on every takeover surface (replaces the old ProjectorLamp). */
export function BrandLockup({ markSize = 22, className }: { markSize?: number; className?: string }) {
  return (
    <span className={className} style={{ display: "inline-flex", alignItems: "center", gap: "calc(var(--spacing) * 2)" }}>
      <BrandMark size={markSize} className="text-foreground" />
      <Wordmark height={Math.round((markSize * 15) / 18)} className="text-foreground" />
    </span>
  );
}
